/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import {
    ICartLineItemViewProps,
    ICartlinesViewProps,
    IOrderSummaryErrors
} from '@msdyn365-commerce-modules/cart';
import { IInvoiceSummaryLines } from '@msdyn365-commerce-modules/invoice-payment-summary';
import { IOrderSummaryLines } from '@msdyn365-commerce-modules/order-summary-utilities';
import { ArrayExtensions } from '@msdyn365-commerce-modules/retail-actions';
import {
    Button,
    getPayloadObject,
    getTelemetryAttributes, ILabeledDropdownOption,
    INodeProps,
    ITelemetryContent, LabeledDropdown,
    Node,
    TelemetryConstant,
    ILabeledDropdownOnChangeNotification
} from '@msdyn365-commerce-modules/utilities';
import { AsyncResult, ChannelInventoryConfiguration } from '@msdyn365-commerce/retail-proxy';
import * as React from 'react';
import {
    ProductListViewMode,
    ProductListViewModeComponent
} from '../../components/product-list-component/components/product-list-view-mode';
import {
    cartLines2ProductList,
    ProductListComponent
} from '../../components/product-list-component/product-list-component';
import { GetCookie, SetCookie } from '../../shared-utils/cookie-utilities';
import { IDlvryCartProps as ICartExtensionProps ,IDlvryCartResources as ICartResources, preferredViewMode } from './dlvry-cart.props.autogenerated';
import { ISortByViewProps } from '../dlvry-search-result-container/dlvry-search-result-container';
import { useState } from 'react';
import { ICartViewProps } from './dlvry-cart';

export interface ICartExtData {
    channelInventoryConfiguration: AsyncResult<ChannelInventoryConfiguration>;
}

const cartViewModeCookieName = 'i_365_cartViewMode_cookie';
const cartSortOptionCookieName = 'i_365_cartSortOptione_cookie';

const getViewModeFromCookie = (props: ICartViewProps): ProductListViewMode | undefined => {
    const viewMode = GetCookie(props.context.request.cookies, cartViewModeCookieName);
    return viewMode ? +viewMode : undefined;
};
const getSortOptionFromCookie = (props: ICartViewProps): string => {
    const SortOption = GetCookie(props.context.request.cookies, cartSortOptionCookieName);
    return SortOption ? SortOption : 'sortByOptionNameAsc';
};

/**
 * Toggle Bopis Function.
 * @param cartLine -CartLine.
 * @param isBopisSelected -Boolean.
 * @returns Toggle Bopis.
 */
const toggleBopisHandler = (cartLine: ICartlinesViewProps, isBopisSelected: boolean) => () => {
    cartLine.pickUpInStore?.callbacks.toggleBopis(!isBopisSelected);
};

/**
 * Change Store Function.
 * @param cartLine -CartLine.
 * @returns Toggle Bopis.
 */
const onChangeStoreHandler = (cartLine: ICartlinesViewProps) => () => {
    cartLine.pickUpInStore?.callbacks.toggleBopis(true);
};

/**
 * Render Bopis block.
 * @param cartLine - The cart line view props.
 * @param resources - Module resource strings.
 * @param storeSelector - Store selector element.
 * @param telemetryContent - The telemetry object.
 * @param emailDeliveryModeCode - The email delivery mode code.
 * @returns JSX Element.
 */
const renderBopisBlock = (
    cartLine: ICartlinesViewProps,
    resources: ICartResources,
    storeSelector: React.ReactNode | undefined,
    telemetryContent?: ITelemetryContent,
    emailDeliveryModeCode?: string
): JSX.Element | null => {
    // If it is electronic item cart line, then return null for BOPISBlock.
    if (!cartLine.pickUpInStore || !storeSelector || cartLine.data?.cartline.DeliveryMode === emailDeliveryModeCode) {
        return null;
    }

    const {
        shipInsteadDisplayText,
        shipToAddressDisplayText,
        pickItUpDisplayText,
        pickUpAtStoreWithLocationText,
        changeStoreDisplayText
    } = resources;

    const isBopisSelected = cartLine.pickUpInStore.isBopisSelected;

    const payLoad = getPayloadObject('click', telemetryContent!, TelemetryConstant.PickupInStore);
    const puckUpinStoreAttribute = getTelemetryAttributes(telemetryContent!, payLoad);

    return (
        <Node {...cartLine.pickUpInStore.ContainerProps}>
            <div className='msc-cart-line__bopis-method'>
                {isBopisSelected ? (
                    <span className='pick-up'>{pickUpAtStoreWithLocationText}</span>
                ) : (
                    <span className='ship'>{shipToAddressDisplayText}</span>
                )}
            </div>
            {isBopisSelected && (
                <div className='msc-cart-line__bopis-fullfilment'>
                    <span className='msc-cart-line__bopis-fullfilment-store'>{cartLine.pickUpInStore.orgUnitName}</span>
                    <Button role='link' className='msc-cart-line__bopis-changestore'
                            onClick={onChangeStoreHandler(cartLine)}>
                        {changeStoreDisplayText}
                    </Button>
                </div>
            )}
            <Button
                className='msc-cart-line__bopis-btn'
                {...puckUpinStoreAttribute}
                onClick={toggleBopisHandler(cartLine, isBopisSelected)}
            >
                {isBopisSelected ? shipInsteadDisplayText : pickItUpDisplayText}
            </Button>
        </Node>
    );
};

/**
 * Render Bopis block.
 * @param cartLine - The cart line view props.
 * @param resources - Module resource strings.
 * @param storeSelector - Store selector element.
 * @param telemetryContent - The telemetry object.
 * @param emailDeliveryModeCode - The email delivery mode code.
 * @returns JSX Element.
 */
const renderGroupBopisBlock = (
    cartLine: ICartlinesViewProps,
    resources: ICartResources,
    storeSelector: React.ReactNode | undefined,
    telemetryContent?: ITelemetryContent,
    emailDeliveryModeCode?: string
): JSX.Element | null => {
    // If it is electronic item cart line, then return null for GroupBOPISBlock.
    if (!cartLine.pickUpInStore || !storeSelector || cartLine.data?.cartline.DeliveryMode === emailDeliveryModeCode) {
        return null;
    }

    const { changeStoreDisplayText, shipInsteadDisplayText, pickItUpDisplayText } = resources;

    const isBopisSelected = cartLine.pickUpInStore.isBopisSelected;

    const payLoad = getPayloadObject('click', telemetryContent!, TelemetryConstant.PickupInStore);
    const puckUpinStoreAttribute = getTelemetryAttributes(telemetryContent!, payLoad);

    return (
        <>
            {isBopisSelected ? (
                <Button role='link' className='msc-cart-line__remove-item msc-btn'
                        onClick={onChangeStoreHandler(cartLine)}>
                    {changeStoreDisplayText}
                </Button>
            ) : null}
            <Button
                role='link'
                className='msc-cart-line__remove-item msc-btn'
                {...puckUpinStoreAttribute}
                onClick={toggleBopisHandler(cartLine, isBopisSelected)}
            >
                {isBopisSelected ? shipInsteadDisplayText : pickItUpDisplayText}
            </Button>
        </>
    );
};

/**
 * Render Cart Lines.
 * @param cartLine - The cart line view props.
 * @param resources - Module resource strings.
 * @param storeSelector - Store selector element.
 * @param telemetryContent - The telemetry object.
 * @param isMultiplePickUp - FLag to check if there multiple pickups.
 * @param emailDeliveryModeCode - The email delivery mode code.
 * @returns JSX Element.
 */
const renderCartLine = (
    cartLine: ICartlinesViewProps,
    resources: ICartResources,
    storeSelector: React.ReactNode | undefined,
    telemetryContent?: ITelemetryContent,
    isMultiplePickUp?: boolean,
    emailDeliveryModeCode?: string
): JSX.Element => {
    if (cartLine.error === resources.outOfStockErrorMessage) {
        return (
            <Node className='msc-cart-line__actions'>
                {cartLine.addToOrderTemplate}
                {cartLine.addToWishlist}
                {cartLine.remove}
            </Node>
        );
    }

    if (isMultiplePickUp) {
        return (
            <Node className='msc-cart-line-group__extra-actions'>
                {renderGroupBopisBlock(cartLine, resources, storeSelector, telemetryContent, emailDeliveryModeCode)}
                {cartLine.addToOrderTemplate}
                {cartLine.addToWishlist}
                {cartLine.remove}
            </Node>
        );
    }

    if (cartLine.addToOrderTemplate) {
        return (
            <Node className='msc-cart-line__extra-actions'>
                {cartLine.addToOrderTemplate}
                {cartLine.addToWishlist}
                {cartLine.remove}
            </Node>
        );
    }

    return (
        <Node className='msc-cart-line__actions'>
            {cartLine.addToWishlist}
            {cartLine.remove}
        </Node>
    );
};

/**
 * Render Cart Lines.
 * @param cartLines - The cart line view props.
 * @param resources - Module resource strings.
 * @param storeSelector - Store selector element.
 * @param telemetryContent - The telemetry object.
 * @param isMultiplePickUp - Flag to check if there multiple pickups.
 * @param emailDeliveryModeCode - The email delivery mode code.
 * @returns JSX Element.
 */
const renderCartlinesInternal = (
    cartLines: ICartlinesViewProps[],
    resources: ICartResources,
    storeSelector: React.ReactNode | undefined,
    telemetryContent?: ITelemetryContent,
    isMultiplePickUp?: boolean,
    emailDeliveryModeCode?: string
): JSX.Element[] | JSX.Element => {
    return cartLines.map((cartLine, index) => {
        const itemIndex = index;
        return (
            <div className='msc-cart-lines-item' key={itemIndex}>
                {cartLine.cartline}
                <span className='msc-cart-lines-button-container'>
                    {isMultiplePickUp
                        ? null
                        : renderBopisBlock(cartLine, resources, storeSelector, telemetryContent, emailDeliveryModeCode)}
                    {renderCartLine(cartLine, resources, storeSelector, telemetryContent, isMultiplePickUp, emailDeliveryModeCode)}
                </span>
            </div>
        );
    });
};

/**
 * Render Cart Lines group title.
 * @param cartLine - The cart line view props.
 * @param resources - Module resource strings.
 * @param count - Count of products.
 * @returns JSX Element.
 */
// Unused per Task 2890
// @ts-ignore
const renderCartLinesGroupTitle = (cartLine: ICartlinesViewProps, resources: ICartResources, count: number) => {
    const isBopisSelected = cartLine.pickUpInStore?.isBopisSelected;
    const { itemLabel, itemsLabel } = resources;
    const itemCountOne: number = 1;
    const suffix = count > itemCountOne ? itemsLabel : itemLabel;
    const countTitle = `(${count} ${suffix})`;
    return (
        <>
            <Node className='msc-cart-lines-group-wrapper__bopis-heading-title'>
                {isBopisSelected ? (
                    <p className='msc-cart-lines-group-wrapper__bopis-heading-title-st'>
                        {cartLine.pickUpInStore?.deliveryOption}
                        {', '}
                        {cartLine.pickUpInStore?.orgUnitName}
                    </p>
                ) : null}
                <p className='msc-cart-lines-group-wrapper__bopis-heading-title-ct'>{countTitle}</p>
            </Node>
        </>
    );
};

/**
 * Render Cart Lines group title.
 * @param cartLine - The cart line view props.
 * @param resources - Module resource strings.
 * @param count - Count of products.
 * @param emailDeliveryModeCode - The email delivery mode code.
 * @returns JSX Element.
 */
const renderCartLinesGroupHeader = (
    cartLine: ICartlinesViewProps,
    resources: ICartResources,
    count: number,
    emailDeliveryModeCode?: string
) => {
    return null;
    // Removed per Task 2890

    /* const isBopisSelected = cartLine.pickUpInStore?.isBopisSelected;
    const { pickUpText, shippingText, emailshippingText } = resources;
    let groupTitle: string;

    groupTitle = isBopisSelected ? pickUpText : shippingText;
    groupTitle = cartLine.data && cartLine.data.cartline.DeliveryMode === emailDeliveryModeCode ? emailshippingText : groupTitle;

    return (
        <>
            <div className='msc-cart-lines-group-wrapper__bopis-heading'>
                <p className={`msc-cart-lines-group-wrapper__bopis-heading-${groupTitle.toLowerCase().replace(' ', '-')}-icon`} />
                <p className={`msc-cart-lines-group-wrapper__bopis-heading-${groupTitle.toLowerCase().replace(' ', '-')}`}>{groupTitle}</p>
                {renderCartLinesGroupTitle(cartLine, resources, count)}
            </div>
        </>
    ); */
};

/**
 * Returns count of products.
 * @param cartLine - The cart line view props.
 * @returns Number of products.
 */
const countItems = (cartLine: ICartlinesViewProps[]): number => {
    let countItem = 0;
    const defaultItemCount = 0;
    countItem = cartLine.reduce((count, item) => {
        return count + (item.data?.cartline.Quantity ?? defaultItemCount);
    }, defaultItemCount);
    return countItem;
};

/**
 * Render error block.
 * @param errorData - Error component.
 * @returns JSX Element.
 */
const renderErrorBlock = (errorData: IOrderSummaryErrors | undefined): JSX.Element | null => {
    if (!errorData || !ArrayExtensions.hasElements(errorData.errors)) {
        return null;
    }
    return (
        <Node {...errorData.Wrapper}>
            {errorData.header}
            {errorData.errors}
        </Node>
    );
};

/**
 * Render invoice summary.
 * @param invoiceSummaryLines - Invoice summary component.
 * @param orderSummaryItems - Order summary component.
 * @param props - The module props.
 * @returns JSX Element.
 */
const renderInvoiceSummarylines = (
    invoiceSummaryLines: IInvoiceSummaryLines | undefined,
    orderSummaryItems: INodeProps,
    props: ICartViewProps
): JSX.Element | null => {
    if (!invoiceSummaryLines) {
        props.context.telemetry.error('InvoiceSummary content is empty, module wont render');
        return null;
    }
    return (
        <Node {...orderSummaryItems}>
            {invoiceSummaryLines.invoices}
            {invoiceSummaryLines.giftCard}
            {invoiceSummaryLines.loyalty}
            {invoiceSummaryLines.orderTotal}
        </Node>
    );
};

/**
 * Render order summary.
 * @param orderSummaryLines - Order summary component.
 * @param orderSummaryItems - Order summary items component.
 * @param props - The module props.
 * @returns JSX Element.
 */
const renderOrderSummarylines = (
    orderSummaryLines: IOrderSummaryLines | undefined,
    orderSummaryItems: INodeProps,
    props: ICartViewProps
): JSX.Element | null => {
    if (!orderSummaryLines) {
        props.context.telemetry.error('OrderSummary content is empty, module wont render');
        return null;
    }

    let totalDiscounts = orderSummaryLines.totalDiscounts;

    // Remove '-' sign from price
    if (totalDiscounts && (totalDiscounts as React.ReactElement).props.price < 0) {
        totalDiscounts = React.cloneElement(totalDiscounts as React.ReactElement, {
            price: Math.abs((totalDiscounts as React.ReactElement).props.price)
        });
    }

    const subAndOrderTotal = props.cart?.cart.CartLines
        ?.map(x => (x.TotalAmount || (x.Price! * (x.Quantity || 1))))
        .reduce((a, b) => a + b, 0);

    const subtotal = React.cloneElement(orderSummaryLines.subtotal as React.ReactElement, {
        price: subAndOrderTotal
    });

    const orderTotal = React.cloneElement(orderSummaryLines.orderTotal as React.ReactElement, {
        price: subAndOrderTotal
    });

    return (
        <Node {...orderSummaryItems}>
            {subtotal}
            {/* {totalDiscounts ? totalDiscounts : null} Disabled while price calculation is slow */}
            {orderSummaryLines.shipping}
            {orderSummaryLines.otherCharge}
            {orderSummaryLines.tax}
            {orderTotal}
        </Node>
    );
};

/**
 * Render error block.
 * @param promotions - The module view props.
 * @returns JSX Element.
 */
const renderPromotions = (promotions: ICartLineItemViewProps): JSX.Element | undefined => {
    return (
        <>
            <Node {...promotions.promotionMessageWrapper}>
                {promotions.promotionMessage}
                <Node className='msc-cart__promotion_link-section'>{promotions.promotionSeeAllLink}</Node>
                {promotions.promotionDialog}
            </Node>
        </>
    );
};

/**
 * Render order summary checkout.
 * @param props - The module view props.
 * @returns JSX Element.
 */
const renderOrderSummaryCheckout = (props: ICartViewProps): JSX.Element | undefined => {
    return (
        <Node className='msc-order-summary__checkout'>
            {props.cart?.hasInvoiceLine || !props.config.displayPromoCodeBlock ? null : props.promoCode}
            <Node className='msc-order-summary__checkout__action'>
                {props.checkoutAsSignInUserButton}
                {props.checkoutAsGuestButton}
                {props.expressCheckoutButton && ArrayExtensions.hasElements(props.expressCheckoutButton) ? (
                    <Node {...props.ExpressCheckoutSectionWrapper}>{props.expressCheckoutButton}</Node>
                ) : null}
            </Node>
            {props.createTemplateFromCartButton}
        </Node>
    );
};

/**
 * Render order summary checkout button.
 * @param props - The module view props.
 * @returns JSX Element.
 */
// @ts-expect-error
const renderOrderSummaryCheckoutButton = (props: ICartViewProps): JSX.Element | undefined => {
    return (
        <Node className='msc-order-summary__checkout-button'>
            {props.checkoutAsSignInUserButton}
            {props.checkoutAsGuestButton}
        </Node>
    );
};

/**
 * Render cart view.
 * @param props - The module view props.
 * @returns JSX Element.
 */
const CartView: React.FC<ICartViewProps & ICartExtensionProps<ICartExtData>> = (props: ICartViewProps & ICartExtensionProps<ICartExtData>) => {

    const sortByDropdownOptions: ILabeledDropdownOption[] = [
        { key: 'sortByOptionNameAsc', value: props.resources.sortByOptionNameAsc },
        { key: 'sortByOptionNameDesc', value: props.resources.sortByOptionNameDesc },
        { key: 'sortByOptionPriceAsc', value: props.resources.sortByOptionPriceAsc },
        { key: 'sortByOptionPriceDesc', value: props.resources.sortByOptionPriceDesc },
        { key: 'sortByOptionLineAmountAsc', value: props.resources.sortByOptionLineAmountAsc },
        { key: 'sortByOptionLineAmountDesc', value: props.resources.sortByOptionLineAmountDesc }
    ];
    // Get view mode first from cookies, then from config, then default to list view if none are set
    const [viewMode, setViewMode] = React.useState<ProductListViewMode>(
        getViewModeFromCookie(props)
        ?? (props.config.preferredViewMode && props.config.preferredViewMode === preferredViewMode.listView
            ? ProductListViewMode.listView
            : ProductListViewMode.cardView)
        ?? ProductListViewMode.listView);
    const [sortedBy, setSortedBy] = useState(getSortOptionFromCookie(props));
    const totalItemCountOne: number = 1;
    // @ts-expect-error
    const updatedTitle = (
        <h1 className='msc-cart__heading'>
            {props.resources.cartTitle}
            <span className='msc-cart__heading-count-items'>
                {` (${Number(props.cart?.totalItemsInCart)} ${props.cart?.totalItemsInCart !== totalItemCountOne ? 'items' : 'item'})`}
            </span>
        </h1>
    );

    /**
     * Render Cart Lines.
     * @param props - The cart line props.
     * @param cartLines - The cart line view props.
     * @param resources - Module resource strings.
     * @param storeSelector - Store selector element.
     * @param waitingComponent - Waiting component element.
     * @param cartLoadingStatus - Cart loading status string.
     * @param isCartDataResult - Flag to check if there is cart data.
     * @param telemetryContent - The telemetry object.
     * @param isMultiplePickUp - Flag to check if there multiple pickups.
     * @param emailDeliveryModeCode - The email delivery mode code.
     * @returns JSX Element.
     */
    const renderCartlines = (
        props: ICartViewProps & ICartExtensionProps<ICartExtData>,
        cartLines: ICartlinesViewProps[] | undefined,
        resources: ICartResources,
        storeSelector: React.ReactNode | undefined,
        waitingComponent: React.ReactNode,
        cartLoadingStatus: string,
        isCartDataResult: boolean,
        telemetryContent?: ITelemetryContent,
        isMultiplePickUp?: boolean,
        emailDeliveryModeCode?: string
    ): JSX.Element[] | JSX.Element => {
        if (cartLoadingStatus) {
            return <>{cartLoadingStatus}</>;
        }

        const actionsAvailable = {
            removeAction: true,
            addToWishlistAction: true
        };

        if (cartLines) {
            if (props.searchTerm) {
                cartLines = cartLines
                    .filter(cartLine => cartLine.data?.product?.Name?.toLowerCase().includes(props.searchTerm.toLowerCase())
                        || cartLine.data?.product?.ItemId?.toLowerCase().includes(props.searchTerm.toLowerCase()));
            }

            cartLines?.sort((a, b) => {
                if (sortedBy === 'sortByOptionNameAsc' && a.data?.product?.Name && b.data?.product?.Name) {
                    return a.data?.product?.Name.localeCompare(b.data.product.Name, props.context.request.locale);
                } else if (sortedBy === 'sortByOptionNameDesc' && a.data?.product?.Name && b.data?.product?.Name) {
                    return b.data?.product?.Name.localeCompare(a.data.product.Name, props.context.request.locale);
                } else if (sortedBy === 'sortByOptionPriceAsc' && a.data?.cartline?.Price && b.data?.cartline?.Price) {
                    return a.data.cartline.Price - b.data.cartline.Price;
                } else if (sortedBy === 'sortByOptionPriceDesc' && a.data?.cartline?.Price && b.data?.cartline?.Price) {
                    return b.data.cartline.Price - a.data.cartline.Price;
                } else if (sortedBy === 'sortByOptionLineAmountAsc' && a.data?.cartline.Price && b.data?.cartline.Price && a.data?.cartline.Quantity && b.data?.cartline.Quantity) {
                    return (a.data.cartline.Price * a.data.cartline.Quantity) - (b.data.cartline.Price * b.data.cartline.Quantity);
                } else if (sortedBy === 'sortByOptionLineAmountDesc' && a.data?.cartline.Price && b.data?.cartline.Price && a.data?.cartline.Quantity && b.data?.cartline.Quantity) {
                    return (b.data.cartline.Price * b.data.cartline.Quantity) - (a.data.cartline.Price * a.data.cartline.Quantity);
                } else {
                    return -0;
                }
            });
        }

        // Props.CartlinesWrapper is only required on cart lines and when multiple pickup mode is disabled. This is also required to handle the empty cart styling.
        // Else, empty cart multiple pickup mode is disabled falls under the other cart line wrapper which is creating issue with Empty cart scenario.
        if (cartLines && viewMode === ProductListViewMode.cardView) {
            for (let i = 0; i < cartLines.length; i++) {
                const recId = cartLines[i].data?.product?.RecordId;
                const error = cartLines[i].error;

                if (recId && error) {
                    cartLines[i].cartline = React.cloneElement(cartLines[i].cartline as React.ReactElement, {
                        productError: {
                            RecordId: recId,
                            Error: error
                        }
                    });
                }
            }
            return props.multiplePickUpEnabled ? (
                renderCartlinesInternal(cartLines, resources, storeSelector, telemetryContent, isMultiplePickUp, emailDeliveryModeCode)
            ) : (
                <Node {...props.CartlinesWrapper}>
                    {renderCartlinesInternal(cartLines, resources, storeSelector, telemetryContent, isMultiplePickUp, emailDeliveryModeCode)}
                </Node>
            );
        } else if (cartLines && viewMode === ProductListViewMode.listView) {
            const errors = cartLines
                .filter(x => x.data?.product?.RecordId && x.error)
                .map(x => ({ RecordId: x.data!.product!.RecordId, Error: x.error! }));

            return (
                <ProductListComponent
                    type='cart'
                    products={cartLines2ProductList(cartLines, props.productAvailabilites, props.context, props.activePrices)}
                    simpleProducts={props.data.products.result || []}
                    unitConversions={props.unitConversions}
                    productErrors={errors}
                    customerInformation={props.data.customerInformation.result}
                    context={props.context}
                    resources={{
                        ...props.resources,
                        removeButtonText: props.resources.removeCartButtonText
                    }}
                    moduleType={props.typeName}
                    moduleId={props.id}
                    allowBackNavigation={false}
                    allowMultiSelect
                    telemetryContent={props.telemetryContent!}
                    isPriceMinMaxEnabled={false}
                    cartViewProps={props}
                    actionsAvailable={actionsAvailable}
                    showAvailability
                    showTotalPrices
                />
            );
        }

        return isCartDataResult ? (
            <div className='msc-cart__empty-cart'>
                <div className='msc-cart-line'>{props.slots.emptyCart}</div>
            </div>
        ) : (
            <>{waitingComponent}</>
        );
    };

    /**
     * Render Cart Lines group.
     * @param props - The cart line view props.
     * @param resources - Module resource strings.
     * @returns JSX Element.
     */
    const renderCartLinesGroup = (props: ICartViewProps & ICartExtensionProps<ICartExtData>, resources: ICartResources): JSX.Element | undefined => {
        if (props.cartLinesGroup && ArrayExtensions.hasElements(props.cartLinesGroup)) {
            return (
                <div className='msc-cart-lines-group'>
                    {props.cartLinesGroup.map((cartlines:any) => {
                        return (
                            <div key={cartlines[0].cartlineId} className='msc-cart-lines-group-wraper'>
                                {renderCartLinesGroupHeader(
                                    cartlines[0],
                                    resources,
                                    countItems(cartlines),
                                    props.context.actionContext.requestContext.channel?.EmailDeliveryModeCode
                                )}
                                {renderCartlines(
                                    props,
                                    cartlines,
                                    props.resources,
                                    props.storeSelector,
                                    props.waitingComponent,
                                    props.cartLoadingStatus,
                                    props.cartDataResult,
                                    props.telemetryContent,
                                    props.multiplePickUpEnabled,
                                    props.context.actionContext.requestContext.channel?.EmailDeliveryModeCode
                                )}
                            </div>
                        );
                    })}
                </div>
            );
        }
        return props.cartDataResult ? (
            <div className='msc-cart__empty-cart'>
                <div className='msc-cart-line'>{props.slots.emptyCart}</div>
            </div>
        ) : (
            <>{props.waitingComponent}</>
        );
    };

    const setCardViewMode = () => {
        setViewMode(ProductListViewMode.cardView);
        updateCurrentViewMode(ProductListViewMode.cardView);
    };

    const setListViewMode = () => {
        setViewMode(ProductListViewMode.listView);
        updateCurrentViewMode(ProductListViewMode.listView);
    };

    const updateCurrentViewMode = (_viewMode: ProductListViewMode) => {
        const cookieAge: number = 31536000000;
        const newCookieValue: string = String(_viewMode);
        SetCookie(props.context.request.cookies, newCookieValue, cartViewModeCookieName, cookieAge);
    };
    const updateSortOptionMode = (_SortOption: any) => {
        const cookieAge: number = 31536000000;
        const newCookieValue: string = String(_SortOption);
        SetCookie(props.context.request.cookies, newCookieValue,cartSortOptionCookieName , cookieAge);
    };

    const viewModeProps = {
        viewMode: viewMode,
        cardButtonTxt: 'Varekort',
        listButtonTxt: 'Liste',
        className: 'ms-search-result-container__category-nav-section__view-modes',
        setCardViewMode: setCardViewMode,
        setListViewMode: setListViewMode
    };

    const _updateSortByDropdown = (notification: ILabeledDropdownOnChangeNotification): void => {
        setSortedBy(notification.selectedOption.key);
        updateSortOptionMode(notification.selectedOption.key);
    };
    const _getSortingDropDown = (): ISortByViewProps => {
        const { resources } = props;
        const activeDropdown = sortByDropdownOptions.find((sortOption)=>{
            return sortOption.key===sortedBy;
        });
        const dropdown = (
            <LabeledDropdown
                labelClassname='reviews-list-sort-by'
                labelText={resources.sortByDropdownLabel}
                dropdownId='categorySortByDropdown'
                dropdownClassname='reviews-list-dropdown'
                toggleColor='link'
                dropdownOptions={sortByDropdownOptions}
                selectedOption={activeDropdown}
                onSelectOption={_updateSortByDropdown}
            />
        );
        return {
            SortingContainer: { className: 'ms-search-result-container__Sort-by-category' },
            sortByDropDown: dropdown
        };
    };
    const renderSort = (props: ISortByViewProps, isRecoSearchPage?: string): JSX.Element | null => {
        const { SortingContainer, sortByDropDown } = props;
        if (sortByDropDown && !isRecoSearchPage) {
            return <Node {...SortingContainer}>{sortByDropDown}</Node>;
        }
        return null;
    };
    const isRecoSearchPage = props.context.actionContext.requestContext.query?.recommendation;

    return (
        <div className={props.className} id={props.id} {...props.renderModuleAttributes(props)}>
            {!props.cart?.hasInvoiceLine && props.promotionOptions &&  renderPromotions(props.promotionOptions)}
            <div className={'cart-view-page'}>
                {props.actionBar}
                <div className='cart-view-page__top'>
                    <div className='msc-search-bar-wrapper'>
                        {props.searchBar}
                    </div>
                    <div className='dlvry-cart-sort-and-view-mode-wrapper'>
                        <div className='ms-search-result-wrapper-sort-by-category'>
                            {!isRecoSearchPage && props.cartlines && props.cartlines.length > 0 && renderSort(_getSortingDropDown())}
                        </div>
                        <ProductListViewModeComponent {...viewModeProps} />
                    </div>
                </div>
                {props.multiplePickUpEnabled && viewMode !== ProductListViewMode.listView
                    ? renderCartLinesGroup(props, props.resources)
                    : renderCartlines(
                        props,
                        props.cartlines,
                        props.resources,
                        props.storeSelector,
                        props.waitingComponent,
                        props.cartLoadingStatus,
                        props.cartDataResult
                    )}
            </div>
            {props.orderSummaryHeading && (
                <Node {...props.OrderSummaryWrapper}>
                    <Node className='msc-order-summary__items'>
                        {props.orderSummaryHeading}
                        {props.cart?.hasInvoiceLine
                            ? renderInvoiceSummarylines(props.invoiceSummaryLineitems, props.OrderSummaryItems, props)
                            : renderOrderSummarylines(props.orderSummaryLineitems, props.OrderSummaryItems, props)}
                        {renderErrorBlock(props.OrderSummaryErrors)}
                    </Node>
                    {renderOrderSummaryCheckout(props)}
                </Node>
            )}
            {props.storeSelector}
            {props.importModal}
        </div>
    );
};

export default CartView;
